// Per-parser sample CSV templates for bank statement imports.
// Keyed by bank_account_id (matches supabase/functions/bank-transactions-import-csv/parserRegistry.ts)

export interface SampleTemplate {
  parserName: string;
  fileBaseName: string;
  headers: string[];
  sampleRows: (string | number)[][];
  preambleRows?: (string | number)[][];
}

const CBE_HEADERS = [
  "Value Date",
  "Reference",
  "Narration",
  "Transaction Type",
  "Debit",
  "Credit",
  "Balance",
];
const CBE_ROWS: (string | number)[][] = [
  ["29-Jan-26", "FT2412345", "Salary Deposit", "Credit", "", 50000, 75000],
  ["30-Jan-26", "FT2412367", "ATM Withdrawal", "Debit", 5000, "", 70000],
];

const BOA_HEADERS = ["Date", "Reference", "Description", "Debit", "Credit", "Balance"];
const BOA_ROWS: (string | number)[][] = [
  ["29-Jan-26", "FT2412345", "Salary Deposit", "", 50000, 75000],
  ["30-Jan-26", "FT2412367", "Transfer to Supplier", 25000, "", 45000],
];

const TELEBIRR_HEADERS = [
  "Receipt No.",
  "Completion Time",
  "Details",
  "Transaction Status",
  "Currency",
  "Paid In",
  "Withdrawn",
  "Balance",
];
const TELEBIRR_ROWS: (string | number)[][] = [
  ["CJ12345678", "31/01/2026 19:16:57", "Customer Payment", "Completed", "ETB", 10000, 0, 60000],
  ["CJ12345679", "01/02/2026 09:22:10", "Bill Payment", "Completed", "ETB", 0, 2500, 57500],
];

const AWASH_HEADERS = [
  "Posting Date",
  "Value Date",
  "Description",
  "Debit Amount",
  "Credit Amount",
  "Balance",
];
const AWASH_ROWS: (string | number)[][] = [
  ["01/29/2026", "01/29/2026", "Salary Deposit", "", 50000, 75000],
  ["01/30/2026", "01/30/2026", "Transfer to Supplier", 25000, "", 50000],
];
const AWASH_PREAMBLE: (string | number)[][] = [
  ["Awash Bank - Account Statement"],
  ["Currency Code:", "ETB"],
  [],
];

const WISE_HEADERS = [
  "TransferWise ID",
  "Date",
  "Date Time",
  "Amount",
  "Currency",
  "Description",
  "Payment Reference",
  "Running Balance",
  "Payer Name",
  "Payee Name",
  "Payee Account Number",
  "Merchant",
  "Card Last Four Digits",
  "Card Holder Full Name",
  "Attachment",
  "Note",
  "Total Fees",
  "Exchange From",
  "Exchange To",
  "Exchange Rate",
  "Exchange To Amount",
  "Transaction Type",
  "Transaction Details Type",
];
const wiseRows = (currency: string): (string | number)[][] => [
  [
    "TW-1000001", "29-01-2026", "29-01-2026 10:15:00", 1500, currency,
    "Invoice payment", "INV-2026-001", 12500,
    "Acme GmbH", "ChipChip Ltd", "BE0123456789", "", "", "", "", "", 0, "", "", "", "", "TRANSFER", "DEPOSIT",
  ],
  [
    "TW-1000002", "30-01-2026", "30-01-2026 14:42:11", -250, currency,
    "Office supplies", "PO-558", 12250,
    "ChipChip Ltd", "Office Depot", "", "Office Depot", "1234", "Hannes K.", "", "", 1.5, "", "", "", "", "CARD", "PURCHASE",
  ],
];

const REVOLUT_HEADERS = [
  "Type",
  "Product",
  "Started Date",
  "Completed Date",
  "Description",
  "Amount",
  "Fee",
  "Currency",
  "State",
  "Balance",
];
const REVOLUT_ROWS: (string | number)[][] = [
  ["TRANSFER", "Current", "2026-01-29 09:00:00", "2026-01-29 09:01:12", "Incoming transfer", 1500, 0, "EUR", "COMPLETED", 5000],
  ["CARD_PAYMENT", "Current", "2026-01-30 12:30:00", "2026-01-30 12:30:05", "Restaurant", -45.5, 0, "EUR", "COMPLETED", 4954.5],
];

const BINANCE_HEADERS = ["User_ID", "UTC_Time", "Account", "Operation", "Coin", "Change", "Remark"];
const BINANCE_ROWS: (string | number)[][] = [
  ["12345678", "2026-01-29 10:00:00", "Spot", "Deposit", "USDT", 1000, ""],
  ["12345678", "2026-01-30 14:00:00", "Spot", "Transaction Related", "USDT", -250, "Buy BTC"],
];

const MPESA_HEADERS = [
  "Receipt No.",
  "Completion Time",
  "Initiation Time",
  "Details",
  "Transaction Status",
  "Paid In",
  "Withdrawn",
  "Balance",
  "Balance Confirmed",
  "Reason Type",
  "Other Party Info",
  "Linked Transaction ID",
  "A/C No.",
];
const MPESA_ROWS: (string | number)[][] = [
  ["QABC1234D5", "2026-01-29 10:15:00", "2026-01-29 10:14:55", "Customer Transfer", "Completed", 5000, 0, 25000, "Yes", "Funds Received", "John Doe 254700111222", "", ""],
  ["QABC1234D6", "2026-01-30 11:00:00", "2026-01-30 10:59:50", "Pay Bill", "Completed", 0, 1200, 23800, "Yes", "Business Payment", "Kenya Power 888880", "", "ACC123"],
];

export const SAMPLE_TEMPLATES: Record<string, SampleTemplate> = {
  // CBE
  "0b4743ad-8157-4b1c-873e-6bd9e387650e": { parserName: "AvenirCBEaccount", fileBaseName: "AvenirCBEaccount_template", headers: CBE_HEADERS, sampleRows: CBE_ROWS },
  "a4071be8-005a-4437-b11a-daaabae84257": { parserName: "HannesCBEaccount", fileBaseName: "HannesCBEaccount_template", headers: CBE_HEADERS, sampleRows: CBE_ROWS },
  "c948fff0-7a88-4743-9aab-20ed7a387152": { parserName: "ChipChipCBEaccount", fileBaseName: "ChipChipCBEaccount_template", headers: CBE_HEADERS, sampleRows: CBE_ROWS },
  "f89caddd-e137-4fe7-93a1-267932a95915": { parserName: "SabaCBEaccount", fileBaseName: "SabaCBEaccount_template", headers: CBE_HEADERS, sampleRows: CBE_ROWS },
  "393c43dd-58b9-438a-a2d4-ab473b34c608": { parserName: "YasminCBEaccount", fileBaseName: "YasminCBEaccount_template", headers: CBE_HEADERS, sampleRows: CBE_ROWS },
  "c61a5f52-c890-44d4-a9be-9f488e5805d9": { parserName: "RedietCBEaccount", fileBaseName: "RedietCBEaccount_template", headers: CBE_HEADERS, sampleRows: CBE_ROWS },
  "df78ee46-0e76-46bb-a6f4-d79d05b34847": { parserName: "AmirCBEaccount", fileBaseName: "AmirCBEaccount_template", headers: CBE_HEADERS, sampleRows: CBE_ROWS },
  // BOA
  "fe88b1a3-d0a1-461c-a340-0a4a287d32b3": { parserName: "ChipChipBOAaccount", fileBaseName: "ChipChipBOAaccount_template", headers: BOA_HEADERS, sampleRows: BOA_ROWS },
  "bf0af5f8-a16d-44e6-92ca-c9095567f339": { parserName: "ChipChipBOAaccount", fileBaseName: "ChipChipBOAaccount_template", headers: BOA_HEADERS, sampleRows: BOA_ROWS },
  "06dc7476-7628-41cf-af79-0ca33bfd26fd": { parserName: "YasminBOAaccount", fileBaseName: "YasminBOAaccount_template", headers: BOA_HEADERS, sampleRows: BOA_ROWS },
  "2ab68e14-e6ee-407c-9ca8-250e61854b2b": { parserName: "RedietBOAaccount", fileBaseName: "RedietBOAaccount_template", headers: BOA_HEADERS, sampleRows: BOA_ROWS },
  "23c9c333-1032-432d-a24d-76ae14ce6086": { parserName: "SabaBOAaccount", fileBaseName: "SabaBOAaccount_template", headers: BOA_HEADERS, sampleRows: BOA_ROWS },
  "4e56e1aa-5445-4c2c-acae-ffff5c08beea": { parserName: "AvenirBOAaccount", fileBaseName: "AvenirBOAaccount_template", headers: BOA_HEADERS, sampleRows: BOA_ROWS },
  "673a6c50-6206-4be4-8b05-621fbff06be7": { parserName: "HannesBOAaccount", fileBaseName: "HannesBOAaccount_template", headers: BOA_HEADERS, sampleRows: BOA_ROWS },
  // Telebirr
  "f00137fa-33c7-4096-9987-f5330fc90bdb": { parserName: "ChipChipTelebirraccount", fileBaseName: "ChipChipTelebirraccount_template", headers: TELEBIRR_HEADERS, sampleRows: TELEBIRR_ROWS },
  "bd6ee835-4346-49a0-9a9a-fab65d4c774e": { parserName: "AvenirTelebirraccount", fileBaseName: "AvenirTelebirraccount_template", headers: TELEBIRR_HEADERS, sampleRows: TELEBIRR_ROWS },
  // Revolut
  "3da4d24e-403a-4f09-8b46-3fb6c545ee08": { parserName: "HannesRevolutaccount", fileBaseName: "HannesRevolutaccount_template", headers: REVOLUT_HEADERS, sampleRows: REVOLUT_ROWS },
  // Binance
  "58109ad8-e812-4c63-8fa5-16ea2a2a9837": { parserName: "HannesBinanceaccount", fileBaseName: "HannesBinanceaccount_template", headers: BINANCE_HEADERS, sampleRows: BINANCE_ROWS },
  // Wise
  "ab8c4ef5-017c-49ab-883b-802fcd8037f2": { parserName: "CompanyWiseEUR", fileBaseName: "CompanyWiseEUR_template", headers: WISE_HEADERS, sampleRows: wiseRows("EUR") },
  "a758e2e7-76ca-4d2c-bd70-3b5c85bef414": { parserName: "CompanyWiseUSD", fileBaseName: "CompanyWiseUSD_template", headers: WISE_HEADERS, sampleRows: wiseRows("USD") },
  // M-Pesa
  "4aa769db-0ec9-46aa-9c91-4fe32a0d865a": { parserName: "MPesaaccount", fileBaseName: "MPesaaccount_template", headers: MPESA_HEADERS, sampleRows: MPESA_ROWS },
  // Awash
  "62d4b391-42fc-4013-b94e-6450fbbd7545": { parserName: "AwashChipChipaccount", fileBaseName: "AwashChipChipaccount_template", headers: AWASH_HEADERS, sampleRows: AWASH_ROWS, preambleRows: AWASH_PREAMBLE },
};

function escapeCsvCell(value: string | number): string {
  const s = value === null || value === undefined ? "" : String(value);
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export function buildCsv(template: SampleTemplate): string {
  const rows: (string | number)[][] = [];
  if (template.preambleRows?.length) rows.push(...template.preambleRows);
  rows.push(template.headers);
  rows.push(...template.sampleRows);
  return rows.map((r) => r.map(escapeCsvCell).join(",")).join("\r\n") + "\r\n";
}

export function downloadCsv(template: SampleTemplate): void {
  const csv = buildCsv(template);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${template.fileBaseName}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
